/* Private discovery QA only. The caller owns query/history lifetime and pagination.
 * All text requests, including response bodies, share this FIFO limit of three.
 * Load this asset before the catalog loader and route all its fetches through
 * swDiscoveryStock.requestText(url, signal).
 */
(() => {
  'use strict';
  if (window.swDiscoveryStock) return;

  const runtime = window.swDiscoveryRuntime;
  if (!runtime) return;
  const VERSION = 'verified939-v1';
  const DIMENSIONS = ['brand', 'taste', 'color'];
  const FRESH_MS = 60000;
  const MAX_CACHE = 20;
  const MAX_FALLBACK_PRODUCTS = 5;
  const REQUEST_TIMEOUT_MS = 20000;
  const diagnosticRequests = [];
  let diagnosticSerial = 0;
  function diagnostic(url) {
    let label='request';try{const u=new URL(url,location.origin);label=decodeURIComponent(u.pathname)+(u.searchParams.get('section_id')?' ['+u.searchParams.get('section_id')+']':u.searchParams.get('view')?' ['+u.searchParams.get('view')+']':'');}catch(e){}
    const record={id:++diagnosticSerial,label,state:'queued',started:Date.now()};diagnosticRequests.push(record);if(diagnosticRequests.length>80)diagnosticRequests.shift();return record;
  }
  function mountDiagnostic(){
    if(runtime.mode !== 'draft' || !new URLSearchParams(location.search).has('sw_debug'))return;
    const root=document.querySelector('[data-sw-mix]');if(!root||document.querySelector('[data-sw-discovery-diagnostic]'))return;
    const panel=document.createElement('details');panel.open=true;panel.dataset.swDiscoveryDiagnostic='true';panel.style.cssText='direction:ltr;text-align:left;background:#fff8df;color:#201900;padding:12px;margin:12px;border:1px solid #a88;font:12px monospace;overflow-wrap:anywhere';const title=document.createElement('summary');title.textContent='Discovery draft diagnostic';const body=document.createElement('pre');body.style.cssText='white-space:pre-wrap;max-height:340px;overflow:auto';panel.append(title,body);root.append(panel);
    const draw=()=>{const now=Date.now(),pending=diagnosticRequests.filter(r=>r.state==='queued'||r.state==='fetch'||r.state==='body');body.textContent='Status: '+(root.querySelector('[data-catalog-status]')?.textContent||'')+'\nCards: '+root.querySelectorAll('[data-discovery-card-key]').length+'; active requests: '+active+'; queued: '+queue.length+'; stock ms: '+(root.dataset.catalogStockElapsedMs||'not resolved')+'\n'+[...pending,...diagnosticRequests.filter(r=>r.state==='error').slice(-5),...diagnosticRequests.filter(r=>r.state==='done').slice(-3)].map(r=>r.id+' '+r.state+' '+Math.round(((r.ended||now)-r.started)/1000)+'s '+(r.status||'')+' '+(r.error||'')+' '+r.label).join('\n');};draw();const timer=setInterval(draw,1000);window.addEventListener('pagehide',()=>clearInterval(timer),{once:true});
  }
  if(typeof document!=='undefined'){if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',mountDiagnostic,{once:true});else setTimeout(mountDiagnostic,0);}
  const queue = [];
  const snapshots = new Map();
  let active = 0;
  let serial = 0;
  let configuration = { routesUrl: null, routes: null };
  let configurationRevision = 0;

  function failure(code, message) {
    const error = new Error(message);
    error.code = code;
    return error;
  }

  function abortError() {
    const error = new Error('Discovery request was cancelled.');
    error.name = 'AbortError';
    return error;
  }

  function checkSignal(signal) {
    if (signal && signal.aborted) throw abortError();
  }

  function drain() {
    while (active < 3 && queue.length) {
      const job = queue.shift();
      if (job.signal) job.signal.removeEventListener('abort', job.onAbort);
      if (job.signal && job.signal.aborted) {
        job.reject(abortError());
        continue;
      }
      active++;
      (async () => {
        const requestController=new AbortController();let timedOut=false;
        const parentAbort=()=>requestController.abort();
        if(job.signal)job.signal.addEventListener('abort',parentAbort,{once:true});
        const timer=setTimeout(()=>{timedOut=true;requestController.abort();},REQUEST_TIMEOUT_MS);
        const deadline=new Promise((resolve,reject)=>requestController.signal.addEventListener('abort',()=>reject(timedOut?failure('STOCK_TIMEOUT','Discovery request exceeded 20 seconds.'):abortError()),{once:true}));
        job.diagnostic.state='fetch';
        try {
          checkSignal(job.signal);
          const response = await Promise.race([fetch(job.url, {
            signal: requestController.signal,
            credentials: 'same-origin',
            cache: 'no-store'
          }),deadline]);
          job.diagnostic.status=response.status;job.diagnostic.state='body';
          if (!response.ok) {
            // A failed status still owns a streaming body; cancel it before
            // making this request slot available to another consumer.
            if (response.body && typeof response.body.cancel === 'function') await Promise.race([response.body.cancel(),deadline]);
            throw failure('STOCK_HTTP', 'Discovery request failed: ' + response.status);
          }
          const body = await Promise.race([response.text(),deadline]);
          checkSignal(job.signal);
          job.diagnostic.state='done';job.diagnostic.ended=Date.now();job.resolve(body);
        } catch (error) {
          const resultError=job.signal && job.signal.aborted ? abortError() : timedOut ? failure('STOCK_TIMEOUT','Discovery request exceeded 20 seconds.') : error;
          job.diagnostic.state='error';job.diagnostic.error=resultError.code||resultError.name||'Error';job.diagnostic.ended=Date.now();job.reject(resultError);
        } finally {
          clearTimeout(timer);if(job.signal)job.signal.removeEventListener('abort',parentAbort);
          active--;
          drain();
        }
      })();
    }
  }

  function requestText(url, signal) {
    if (signal && signal.aborted) return Promise.reject(abortError());
    return new Promise((resolve, reject) => {
      const job = { url, signal, resolve, reject, onAbort: null, diagnostic: diagnostic(url) };
      job.onAbort = () => {
        const position = queue.indexOf(job);
        if (position < 0) return; // An active fetch owns its own cancellation.
        queue.splice(position, 1);job.diagnostic.state='error';job.diagnostic.error='AbortError';job.diagnostic.ended=Date.now();
        signal.removeEventListener('abort', job.onAbort);
        reject(abortError());
      };
      if (signal) signal.addEventListener('abort', job.onAbort, { once: true });
      queue.push(job);
      drain();
    });
  }

  function configure(options) {
    if (!options || typeof options !== 'object') throw failure('STOCK_CONFIG', 'Stock configuration is required.');
    const routesUrl = options.routesUrl == null ? null : String(options.routesUrl);
    const routes = options.routes == null ? null : JSON.parse(JSON.stringify(options.routes));
    if (!routes && !routesUrl) throw failure('STOCK_CONFIG', 'Provide routes or routesUrl.');
    // Calling configure repeatedly from separate consumers must not invalidate
    // a live cache when all use the same already configured asset URL.
    if (!routes && routesUrl === configuration.routesUrl) return;
    configuration = { routesUrl, routes };
    configurationRevision++;
    snapshots.clear();
  }

  function isId(value) {
    return typeof value === 'string' && /^[1-9][0-9]*$/.test(value);
  }

  function productPath(value) {
    if (typeof value !== 'string') throw failure('STOCK_INDEX', 'Missing reviewed product path.');
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || !/^\/products\/[^/]+\/?$/.test(url.pathname) || url.search || url.hash) {
      throw failure('STOCK_INDEX', 'Invalid reviewed product path.');
    }
    return url;
  }

  function inspect(index, selected, cards) {
    if (!index || index.version !== VERSION || !Array.isArray(index.cards) || index.cards.length !== 940 ||
        !Array.isArray(index.options) || index.options.length !== 140) {
      throw failure('STOCK_INDEX', 'Stock requires the verified939-v1 catalog.');
    }
    const choices = new Map();
    for (const option of index.options) {
      if (!option || !DIMENSIONS.includes(option.dimension) || typeof option.value !== 'string' ||
          !/^[a-z0-9][a-z0-9-]*$/.test(option.value)) throw failure('STOCK_INDEX', 'Invalid catalog option.');
      const key = option.dimension + ':' + option.value;
      if (choices.has(key)) throw failure('STOCK_INDEX', 'Duplicate catalog option.');
      choices.set(key, []);
    }
    const identities = new Map();
    const products = new Map();
    for (const card of index.cards) {
      if (!card || !isId(card.productId) || typeof card.key !== 'string' || identities.has(card.key)) {
        throw failure('STOCK_INDEX', 'Invalid or duplicate catalog identity.');
      }
      if (card.variantId != null && !isId(card.variantId)) throw failure('STOCK_INDEX', 'Invalid variant identity.');
      if (card.key !== card.productId + (card.variantId ? ':' + card.variantId : '')) {
        throw failure('STOCK_INDEX', 'Catalog key does not match its exact identity.');
      }
      productPath(card.path);
      if (products.has(card.productId) && products.get(card.productId).path !== card.path) {
        throw failure('STOCK_INDEX', 'A product has inconsistent reviewed paths.');
      }
      identities.set(card.key, card);
      products.set(card.productId, card);
      for (const dimension of DIMENSIONS) {
        if (!Array.isArray(card[dimension]) || new Set(card[dimension]).size !== card[dimension].length) {
          throw failure('STOCK_INDEX', 'Invalid catalog dimension.');
        }
        for (const value of card[dimension]) {
          const rows = choices.get(dimension + ':' + value);
          if (!rows) throw failure('STOCK_INDEX', 'Unreviewed catalog option.');
          rows.push(card);
        }
      }
    }
    if (products.size !== 939) throw failure('STOCK_INDEX', 'Catalog product coverage is not 939.');
    const selection = {};
    if (selected == null) selected = {};
    if (typeof selected !== 'object' || Array.isArray(selected) || Object.keys(selected).some(key => !DIMENSIONS.includes(key) && key !== 'matchMode')) {
      throw failure('STOCK_SELECTION', 'Invalid selection dimensions.');
    }
    const matchMode = selected.matchMode;
    if (matchMode !== undefined && !['all', 'any'].includes(matchMode)) {
      throw failure('STOCK_SELECTION', 'Invalid match mode.');
    }
    for (const dimension of DIMENSIONS) {
      const values = selected[dimension] == null ? [] : selected[dimension];
      if (!Array.isArray(values) || values.some(value => !choices.has(dimension + ':' + value))) {
        throw failure('STOCK_SELECTION', 'Selection includes an unreviewed option.');
      }
      selection[dimension] = [...new Set(values)].sort();
    }
    const seen = new Set();
    const expected = index.cards.filter(card => {
      const tests = DIMENSIONS.flatMap(dimension => selection[dimension].map(value => card[dimension].includes(value)));
      const matches = matchMode === 'all' ? tests.every(Boolean) : matchMode === 'any' ?
        !tests.length || tests.some(Boolean) : DIMENSIONS.every(dimension => !selection[dimension].length ||
          selection[dimension].some(value => card[dimension].includes(value)));
      if (!matches) return false;
      if (!selection.taste.length && seen.has(card.productId)) return false;
      seen.add(card.productId);
      return true;
    });
    if (!Array.isArray(cards) || cards.length !== expected.length ||
        cards.some((card, position) => !card || card.key !== expected[position].key ||
          card.productId !== expected[position].productId || card.path !== expected[position].path ||
          card.variantId !== expected[position].variantId)) {
      throw failure('STOCK_MATCH', 'Requested cards are not the exact reviewed selection in catalog order.');
    }
    return {
      index, selection, matchMode, choices, identities, products, cards: expected,
      queryKey: VERSION + '|' + DIMENSIONS.map(dimension => dimension + '=' + selection[dimension].join(',')).join('&') +
        (matchMode === undefined ? '' : '|match=' + matchMode)
    };
  }

  function sameIds(actual, expected, code) {
    if (!Array.isArray(actual) || actual.some(id => !isId(id)) || new Set(actual).size !== actual.length ||
        actual.length !== expected.size || actual.some(id => !expected.has(id))) {
      throw failure(code, 'Stock membership does not equal the complete reviewed product set.');
    }
  }

  async function routesFor(context, signal, revision) {
    const current = configuration;
    let manifest = current.routes;
    if (!manifest) {
      if (!current.routesUrl) throw failure('STOCK_CONFIG', 'Stock routes have not been configured.');
      manifest = JSON.parse(await requestText(current.routesUrl, signal));
    }
    checkSignal(signal);
    if (revision !== configurationRevision) throw failure('STOCK_CONFIG_CHANGED', 'Stock route configuration changed.');
    if (!manifest || (manifest.version || manifest.cohortVersion) !== VERSION ||
        (manifest.version && manifest.cohortVersion && manifest.version !== manifest.cohortVersion) ||
        !manifest.routes || typeof manifest.routes !== 'object' || Array.isArray(manifest.routes)) {
      throw failure('STOCK_ROUTES', 'Stock route version or shape is invalid.');
    }
    const routes = new Map();
    for (const [key, route] of Object.entries(manifest.routes)) {
      if (!context.choices.has(key) || !route || typeof route.view !== 'string' ||
          !runtime.allowedView(route.view) || (runtime.mode !== 'production' &&
          (typeof route.sectionId !== 'string' || !/^[a-zA-Z0-9_-]+$/.test(route.sectionId)))) {
        throw failure('STOCK_ROUTES', 'Stock route is not an allowed private QA leaf.');
      }
      const expected = new Set(context.choices.get(key).map(card => card.productId));
      sameIds(route.productIds, expected, 'STOCK_ROUTE_COVERAGE');
      routes.set(key, { key, view: route.view, sectionId: route.sectionId, productIds: [...expected] });
    }
    // Cache only successful validated configuration. Do not share an in-flight
    // caller-owned fetch: aborting an older query must not abort a new query.
    configuration.routes = manifest;
    return routes;
  }

  function chooseCover(context, routes) {
    if (context.matchMode === 'any') {
      const keys = DIMENSIONS.flatMap(dimension => context.selection[dimension].map(value => dimension + ':' + value));
      if (!keys.length || !keys.every(key => routes.has(key))) {
        throw failure('STOCK_ROUTE_COVER', 'No complete union stock cover is available.');
      }
      const leaves = keys.map(key => routes.get(key));
      const productIds = [...new Set(leaves.flatMap(route => route.productIds))];
      const covered = new Set(productIds);
      if (context.cards.some(card => !covered.has(card.productId))) {
        throw failure('STOCK_ROUTE_COVER', 'Union stock cover omits a matched product.');
      }
      return { dimension: 'any', keys, routes: leaves, productIds };
    }
    const candidates = [];
    for (const dimension of DIMENSIONS) {
      const values = context.selection[dimension];
      if (!values.length) continue;
      const keys = values.map(value => dimension + ':' + value);
      if (!keys.every(key => routes.has(key))) continue; // Never use a partial OR union.
      const leaves = keys.map(key => routes.get(key));
      const productIds = [...new Set(leaves.flatMap(route => route.productIds))];
      candidates.push({ dimension, keys, routes: leaves, productIds });
    }
    candidates.sort((a, b) => a.productIds.length - b.productIds.length || a.keys.length - b.keys.length);
    const cover = candidates[0];
    if (!cover) throw failure('STOCK_ROUTE_COVER', 'No complete selected-dimension stock cover is available.');
    const covered = new Set(cover.productIds);
    if (context.cards.some(card => !covered.has(card.productId))) throw failure('STOCK_ROUTE_COVER', 'Stock cover omits a matched product.');
    return cover;
  }

  function payload(html, attribute) {
    const document = new DOMParser().parseFromString(html, 'text/html');
    const scripts = document.querySelectorAll('script[type="application/json"][' + attribute + ']');
    if (scripts.length !== 1) throw failure('STOCK_WRAPPER', 'Expected one exact stock JSON wrapper.');
    return JSON.parse(scripts[0].textContent);
  }

  function identity(data) {
    if (!runtime.identity(data)) {
      throw failure('STOCK_THEME', 'Stock response is not from the exact approved discovery runtime.');
    }
  }

  function canonicalEmbeddedSection(data, route) {
    // Pagination templates have their own rendered section IDs. Their stock
    // must not be adopted as the canonical leaf section. Check trust identity
    // first so a section mismatch cannot hide a wrong-theme/version payload.
    identity(data);
    if (data.cohortVersion !== VERSION) throw failure('STOCK_VERSION', 'Stock response cohort version differs.');
    if (runtime.mode === 'production') {
      if (data.pageHandle !== 'discover') throw failure('STOCK_ROUTE_IDENTITY', 'Stock resource differs.');
      return data.templateSuffix === route.view;
    }
    return data.sectionId === route.sectionId;
  }

  function captureTime(data, requestStarted) {
    const now = Date.now();
    if (!/^\d{10,}$/.test(String(data.generatedAt))) throw failure('STOCK_TIME', 'Stock response has no generation time.');
    const generated = Number(data.generatedAt) * 1000;
    if (!Number.isSafeInteger(generated) || generated > now + 5000 || now - generated > FRESH_MS) {
      throw failure('STOCK_STALE', 'Stock response is outside the freshness window.');
    }
    return Math.min(requestStarted, generated, now);
  }

  function validateRows(data, expectedIds, route, requestStarted) {
    identity(data);
    if (data.cohortVersion !== VERSION) throw failure('STOCK_VERSION', 'Stock response cohort version differs.');
    if (runtime.mode === 'production' && (data.pageHandle !== 'discover' ||
        data.templateSuffix !== (route ? route.view : runtime.registryView))) {
      throw failure('STOCK_ROUTE_IDENTITY', 'Stock page or template identity differs.');
    }
    if (route && (data.sliceKey !== route.key || (runtime.mode !== 'production' && data.sectionId !== route.sectionId))) {
      throw failure('STOCK_ROUTE_IDENTITY', 'Stock slice or rendered section identity differs.');
    }
    if (data.expectedProducts !== expectedIds.size || !Array.isArray(data.rows) || data.rows.length !== expectedIds.size) {
      throw failure('STOCK_COVERAGE', 'Stock response is incomplete.');
    }
    const rows = new Map();
    for (const row of data.rows) {
      if (!Array.isArray(row) || row.length !== 3 || !isId(row[0]) || typeof row[1] !== 'boolean' ||
          !Array.isArray(row[2]) || rows.has(row[0]) || !expectedIds.has(row[0])) {
        throw failure('STOCK_ROW', 'Invalid, unexpected, or duplicate stock product row.');
      }
      const flags = new Map([[row[0], row[1]]]);
      for (const variant of row[2]) {
        if (!Array.isArray(variant) || variant.length !== 2 || !isId(variant[0]) || typeof variant[1] !== 'boolean' ||
            flags.has(row[0] + ':' + variant[0])) {
          throw failure('STOCK_VARIANT', 'Invalid, unknown, or duplicate exact-variant availability.');
        }
        flags.set(row[0] + ':' + variant[0], variant[1]);
      }
      rows.set(row[0], flags);
    }
    return { rows, capturedAt: captureTime(data, requestStarted) };
  }

  function urlFor(route) {
    const url = runtime.url(runtime.pagePath);
    url.searchParams.set('view', route ? route.view : runtime.registryView);
    if (route && runtime.mode !== 'production') url.searchParams.set('section_id', route.sectionId);
    return url;
  }

  function mergeRows(results) {
    const products = new Map();
    const flags = new Map();
    let capturedAt = Date.now();
    for (const result of results) {
      capturedAt = Math.min(capturedAt, result.capturedAt);
      for (const [productId, row] of result.rows) {
        const old = products.get(productId);
        if (old && (old.size !== row.size || [...old].some(([key, available]) => row.get(key) !== available))) {
          throw failure('STOCK_CONFLICT', 'Overlapping stock leaves disagree; retry the query.');
        }
        products.set(productId, row);
        for (const [key, available] of row) flags.set(key, available);
      }
    }
    return { products, flags, capturedAt };
  }

  async function fillMissingVariants(context, stock, signal) {
    const missing = context.cards.filter(card => !stock.flags.has(card.key));
    if (!missing.length) return;
    if (missing.some(card => !card.variantId || !stock.products.has(card.productId))) {
      throw failure('STOCK_MISSING', 'A matched product has no verified stock row.');
    }
    const products = [...new Map(missing.map(card => [card.productId, card])).values()];
    if (products.length > MAX_FALLBACK_PRODUCTS) throw failure('STOCK_FALLBACK_CAP', 'Too many missing exact identities.');
    await Promise.all(products.map(async card => {
      const started = Date.now();
      const url = runtime.url(productPath(card.path).href);
      url.searchParams.set('section_id', runtime.productStockSection);
        const data = payload(await requestText(url, signal), 'data-discovery-product-stock');
      identity(data);
      if (data.productId !== card.productId || typeof data.available !== 'boolean' || !Array.isArray(data.variants)) {
        throw failure('STOCK_FALLBACK', 'Product-context stock identity differs.');
      }
      const flags = new Map();
      for (const variant of data.variants) {
        if (!Array.isArray(variant) || variant.length !== 2 || !isId(variant[0]) || typeof variant[1] !== 'boolean' || flags.has(variant[0])) {
          throw failure('STOCK_VARIANT', 'Invalid product-context variant flag.');
        }
        flags.set(variant[0], variant[1]);
      }
      const old = stock.products.get(card.productId);
      if (old.get(card.productId) !== data.available || [...old].some(([key, value]) =>
          key !== card.productId && flags.get(key.split(':')[1]) !== value)) {
        throw failure('STOCK_CONFLICT', 'Product-context stock changed during this query.');
      }
      for (const missingCard of missing.filter(item => item.productId === card.productId)) {
        if (!flags.has(missingCard.variantId)) throw failure('STOCK_MISSING', 'Reviewed exact variant is still missing.');
        stock.flags.set(missingCard.key, flags.get(missingCard.variantId));
      }
      stock.capturedAt = Math.min(stock.capturedAt, captureTime(data, started));
    }));
  }

  function freezeCard(card) {
    const copy = { ...card };
    for (const dimension of DIMENSIONS) copy[dimension] = Object.freeze([...card[dimension]]);
    return Object.freeze(copy);
  }

  function snapshot(context, stock, cover, cacheHit, existingId) {
    const available = [];
    const unavailable = [];
    const flags = {};
    for (const card of context.cards) {
      const flag = stock.flags.get(card.key);
      if (typeof flag !== 'boolean') throw failure('STOCK_MISSING', 'A matched card has unknown availability.');
      flags[card.key] = flag;
      (flag ? available : unavailable).push(freezeCard(card));
    }
    return Object.freeze({
      id: existingId || VERSION + ':' + Date.now() + ':' + (++serial),
      cohortVersion: VERSION,
      queryKey: context.queryKey,
      capturedAt: stock.capturedAt,
      cards: Object.freeze(available.concat(unavailable)),
      availability: Object.freeze(flags),
      cover: Object.freeze({
        dimension: cover.dimension,
        keys: Object.freeze([...cover.keys]),
        productIds: Object.freeze([...cover.productIds])
      }),
      cacheHit
    });
  }

  async function resolve(index, selected, cards, signal, options) {
    checkSignal(signal);
    const context = inspect(index, selected, cards);
    const revision = configurationRevision;
    // No request at all for the exact empty intersection, including no routes.
    if (!context.cards.length) {
      return snapshot(context, { flags: new Map(), capturedAt: Date.now() }, { dimension: null, keys: [], productIds: [] }, false);
    }
    const key = revision + '|' + context.queryKey;
    const cached = snapshots.get(key);
    let useCached = cached && Date.now() - cached.capturedAt <= FRESH_MS && Date.now() >= cached.capturedAt;
    if (useCached) {
      const embedded = options && options.embedded;
      if (embedded && cached.cover.keys.length === 1 && embedded.sliceKey === cached.cover.keys[0]) {
        const route = configuration.routes && configuration.routes.routes[cached.cover.keys[0]];
        if (route) {
          if (!canonicalEmbeddedSection(embedded, route)) useCached = false;
          else {
            try {
              validateRows(embedded, new Set(cached.cover.productIds), { ...route, key: embedded.sliceKey }, Date.now());
            } catch (error) {
              // A still-fresh retained snapshot may outlive its original SSR
              // markup, but cached data must not hide an identity/coverage error.
              if (error.code !== 'STOCK_STALE' && error.code !== 'STOCK_TIME') throw error;
            }
          }
        }
      }
    }
    if (useCached) {
      snapshots.delete(key);
      snapshots.set(key, cached);
      return Object.freeze({ ...cached, cacheHit: true });
    }
    if (cached) snapshots.delete(key);
    const controller = new AbortController();
    const onAbort = () => controller.abort();
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    const operationSignal = controller.signal;
    try {
      checkSignal(signal);
      const hasSelection = DIMENSIONS.some(dimension => context.selection[dimension].length);
      let cover;
      let results;
      if (hasSelection) {
        const routes = await routesFor(context, operationSignal, revision);
        cover = chooseCover(context, routes);
        const embedded = options && options.embedded;
        if (embedded && cover.routes.length === 1 && embedded.sliceKey === cover.keys[0] &&
            canonicalEmbeddedSection(embedded, cover.routes[0])) {
          try {
            results = [validateRows(embedded, new Set(cover.productIds), cover.routes[0], Date.now())];
          } catch (error) {
            // Old SSR markup is not a new-query stock snapshot. Fetch current
            // truth instead. Matching wrong-theme/version/coverage fails closed.
            if (error.code !== 'STOCK_STALE' && error.code !== 'STOCK_TIME') throw error;
          }
        }
        if (!results) {
          results = await Promise.all(cover.routes.map(async route => {
            const started = Date.now();
            const data = payload(await requestText(urlFor(route), operationSignal), 'data-discovery-stock-slice');
            return validateRows(data, new Set(route.productIds), route, started);
          }));
        }
      } else {
        cover = { dimension: null, keys: [], productIds: [...context.products.keys()] };
        const started = Date.now();
        const data = payload(await requestText(urlFor(null), operationSignal), 'data-discovery-stock-registry');
        results = [validateRows(data, new Set(cover.productIds), null, started)];
      }
      const stock = mergeRows(results);
      sameIds([...stock.products.keys()], new Set(cover.productIds), 'STOCK_COVERAGE');
      await fillMissingVariants(context, stock, operationSignal);
      checkSignal(operationSignal);
      if (revision !== configurationRevision) throw failure('STOCK_CONFIG_CHANGED', 'Stock route configuration changed.');
      if (Date.now() - stock.capturedAt > FRESH_MS) throw failure('STOCK_STALE', 'Stock expired while preparing the query.');
      const result = snapshot(context, stock, cover, false);
      snapshots.set(key, result);
      while (snapshots.size > MAX_CACHE) snapshots.delete(snapshots.keys().next().value);
      return result;
    } catch (error) {
      controller.abort(); // Stop sibling leaf/fallback requests and remove queued work.
      throw signal && signal.aborted ? abortError() : error;
    } finally {
      if (signal) signal.removeEventListener('abort', onAbort);
    }
  }

  function adopt(index, selected, cards, raw, signal) {
    return resolve(index, selected, cards, signal, { embedded: raw });
  }

  window.swDiscoveryStock = Object.freeze({ configure, resolve, adopt, requestText });
})();
